import React from 'react';
import styled from 'styled-components';
import {useHistory} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {Icon, Link, SubHeading, Text, WorkSignifier, tokensForThemeProvider} from '@forecast-it/design-system';
import {CellValue, RagState, RowType} from '../../data/reportRowModel';
import {EMPTY_VALUE_MESSAGE_ID_BY_PATH, isUnestimated, isUnsettableRow} from '../emptyValueLabels';
import {UnestimatedNote} from '../MutedNote';
import {formatDuration} from '../durationFormat';
import {TruncatedCellContent} from './TruncatedCellContent';
import {isModifiedClick} from '../../../../forecast-app/shared/util/LinkClickUtil';
import Person from '../../../../forecast-app/shared/components/person/person';
import TooltipContainer from '../../../../forecast-app/shared/components/tooltips/tooltip_container';
import LabelGroup from '../../../../forecast-app/shared/components/labels/label_group';

const tokens = tokensForThemeProvider.sky.sys;

/** Icon glyph + DS icon-color token per RAG state, matching the icon choice `RagStatusMessage`
 * itself uses internally (RagStatusMessage.js `getIcon`) — green/checkCircle, amber/warning,
 * red/alert - but WITHOUT that component's bordered-box chrome (see `StatusCellWrapper` below).
 *
 * The fourth state maps to an explicit `null`, not to a missing key: a traffic light beside the em
 * dash would read as a judgement about the project, when the whole point of that state is that we
 * have not got one. A TOTAL record rather than a `Partial` so a state added later fails to compile
 * until someone decides here whether it has an icon - a `Partial` would let it default silently to
 * no icon, which is a judgement in itself. */
const RAG_ICON: Record<RagState, {icon: string; color: string} | null> = {
	on_track: {icon: 'checkCircle', color: 'success'},
	at_risk: {icon: 'warning', color: 'warning'},
	off_track: {icon: 'alert', color: 'error'},
	unknown: null,
};

const NotAvailable = (): JSX.Element => (
	<Text size={2} color="secondary">
		N/A
	</Text>
);

/** An audit row's empty cell. Audit rows are sparse by design (a week approval has no project, a
 * status change no edited field), so "—" reads as "nothing here" where N/A reads as broken. */
const AuditEmpty = (): JSX.Element => (
	<Text size={2} color="secondary">
		—
	</Text>
);

/** A task cell nobody has filled in, named for what is missing ("No assignee") rather than `N/A`,
 * which says the field does not apply. Same muted look as `N/A`, so the two read as siblings. */
const Unset = ({messageId}: {messageId: string}): JSX.Element => {
	const {formatMessage} = useIntl();
	return (
		<Text size={2} color="secondary">
			{formatMessage({id: messageId})}
		</Text>
	);
};

/** A boolean cell, rendered as the shared Yes/No words rather than a raw `true`/`false`. Its own
 * component (like `Unset` above) so `useIntl` is only called for rows that actually need it -
 * most `CellRenderer` callers render outside an `IntlProvider` ancestor. */
const BooleanCell = ({value}: {value: boolean}): JSX.Element => {
	const {formatMessage} = useIntl();
	return <Text size={2}>{formatMessage({id: value ? 'common.yes' : 'common.no'})}</Text>;
};

/**
 * What a reader is shown in place of an empty retainer figure, per reason the row gave.
 *
 * A retainer column can be empty for four different reasons and, until these words existed, all four
 * looked identical. The words are owned here rather than published by report-service on purpose: the
 * catalog's `notApplicableRules` are tokens to render, the same way its field `values` are - "the
 * tokens a filter condition carries, not labels to show a user".
 *
 * A project that is not on a retainer gets the em dash rather than a sentence, because the Budget
 * type column already says why, and repeating it in six columns of every non-retainer row would
 * shout the least interesting fact on the report. The other three each say something the row cannot
 * otherwise tell you.
 *
 * A reason not in this map, and a cell with no reason at all, both fall through to `N/A` - which is
 * the honest rendering for "we could not read this", the one case the four reasons deliberately do
 * not cover.
 *
 * A `Map` for the same reason `GRAIN_SHAPERS` is one: the key is a token off the wire, and a plain
 * object answers `constructor`/`toString`/`valueOf` off its prototype - handing back a function that
 * is neither undefined nor renderable, so the fallback below would be skipped and React would be
 * given a function as a child.
 */
const NOT_APPLICABLE_LABELS = new Map<string, string>([
	['project.notOnRetainer', '—'],
	['project.noRetainerPeriodInRange', 'No period'],
	['project.retainerPeriodNotFixedHours', 'No target'],
	['project.retainerPeriodNotStarted', 'Not started'],
]);

/** An empty cell that says why it is empty, falling back to `N/A` when nothing explains it. */
const NotApplicable = ({reason}: {reason?: string}): JSX.Element => {
	const label = reason === undefined ? undefined : NOT_APPLICABLE_LABELS.get(reason);
	if (label === undefined) {
		return <NotAvailable />;
	}
	return (
		<Text size={2} color="secondary">
			{label}
		</Text>
	);
};

/** the design renders the task status cell as plain inline text + icon — no
 * border, no box, natural (shrink-to-fit) width. The DS `RagStatusMessage` component always draws
 * a colored bordered box around its content (`RagStatusMessageWrapper` in RagStatusMessage.js) and
 * has no compact/inline variant (RagStatusMessage.d.ts exposes only `status`/`children`/
 * `iconOnLeft`), so it does not match this design — reusing it here would mean re-overriding its
 * own chrome via CSS overrides, which is worse than composing the same DS primitives (`Icon` +
 * `Text`) the rest of this file already uses. `inline-flex` keeps icon+label on one line at their
 * natural width instead of stretching to the table cell. */
const StatusCellWrapper = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.spacing['100']};
`;

/** the design's project cell is the colored id pill (`WorkSignifier`) followed by the project
 * name. `WorkSignifier` renders only the id chip inline (name is its hover title), so the visible
 * name is a `Text` beside it. `inline-flex` keeps pill + name on one line at natural width. */
const ProjectCellWrapper = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.spacing['100']};
`;

const ProjectCell = ({
	id,
	name,
	projectKey,
	color,
	href,
}: {
	id: string;
	name: string;
	projectKey: string;
	color: string;
	href: string | null;
}): JSX.Element => {
	// Called unconditionally on every render of this component, regardless of whether `href` is
	// null, so this hook is never called conditionally.
	const history = useHistory();

	// Plain left-click routes in-app so the unsaved-changes overlay can intercept it; a modified
	// click falls through to the browser, which is how a user consciously opens a new tab. The
	// real href is what makes that fall-through work and what gives the browser its own link
	// affordances (open in new tab/window, copy address, status-bar preview).
	const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
		if (isModifiedClick(event)) {
			return;
		}
		event.preventDefault();
		// Only wired to the anchor in the `href` branch below, so `href` is never null here.
		if (href !== null) {
			history.push(href);
		}
	};

	return (
		<ProjectCellWrapper>
			<WorkSignifier workId={id} name={`${projectKey} ${name}`.trim()} color={color} />
			{/* The name is the link to the project page; the id pill beside it stays inert. A
			    project with no usable id has no page to open, and falls back to the emphasised
			    static name. Both carry size 2 (14px), the table's text scale — size 1 is 16px and
			    would render this one column larger than every other cell in the row. */}
			{name ? (
				<TruncatedCellContent tooltip={name}>
					{href ? (
						<Link href={href} onClick={handleClick} size={'2'}>
							{name}
						</Link>
					) : (
						<SubHeading size={'2'}>{name}</SubHeading>
					)}
				</TruncatedCellContent>
			) : null}
		</ProjectCellWrapper>
	);
};

// Matches the Project Portfolio Standard Report's owner rendering: a single owner shows its name
// beside the avatar; two or more collapse to avatars alone, with each full name available in a
// tooltip on hover.
//
// This cell is shared, so the collapse applies to task assignees too, not only project owners -
// a deliberate choice rather than an oversight. Multi-person cells read the same way everywhere,
// and the fixed row height is what forces it: two full names inline overflow the column's default
// width at any row height this table allows. It is a visible change for the task grain, where a
// two-assignee row previously read as inline names; the names remain one hover away, and an
// assignee entry carries no profile picture on the wire, so such a row becomes two initials
// avatars. Deliberately not copied from that report: the `ProjectContact` styled wrapper
// (a layout concern of that report's own column, not this one) and the tooltip's
// `timer={5 * 60 * 1000}` (a show-delay tuned for that report's hover behaviour).
//
// `span.tooltip-container` (rendered by `TooltipContainer`) is inline, but the `div.forecast-person`
// it wraps is `display: flex`, so a block box would break the inline flow and stack multiple owners
// vertically. The standard report avoids this because its column is itself a flex row; this table's
// cell is a plain `<td>`, so `PersonsCellWrapper` supplies the flex row here instead.
/** Hosts `LabelGroup` so its chips size to their own text and start at the cell's left edge.
 *
 * Two things have to be neutralised, both because `LabelNamed` was written for the standard
 * report's legacy table rather than for this one:
 *
 * `line-height: normal` - the chip is a fixed 20px tall with 8px of vertical padding, leaving a
 * 12px content box, and its `.label-name` declares no line-height of its own. Inside this surface's
 * design-system typography the 10px label text inherits a 20px line box, which overflows that 12px
 * box, and `.label-name`'s own `overflow: hidden` (there for its ellipsis) then clips the ascenders
 * and descenders. The standard report escapes this only because it is not inside DS typography, so
 * the same chip inherits `normal` there.
 *
 * The negative margin cancels `LabelNamed`'s own `margin-left: 12px`, which is spacing meant for
 * chips sitting in a row after something else. Here the first chip is the cell's first content, and
 * that margin left it indented against every other column. */
const LabelsCellWrapper = styled.span`
	/* flex with a real width, NOT inline-flex: LabelGroup's own container is width:100% with
	   overflow:hidden, and inside a shrink-to-fit inline box that percentage has nothing to resolve
	   against, so the group collapses and clips its own chips however wide the column is. */
	display: flex;
	width: 100%;
	align-items: center;
	line-height: normal;

	> * {
		margin-left: -12px;
	}
`;

const PersonsCellWrapper = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.spacing['100']};

	/* The initials hexagon draws its top and bottom points as pseudo-elements positioned OUTSIDE
	   its own box, and reserves room for them with vertical margins equal to their height - at the
	   size used here a 13.28px band plus 6.64px each side, so a 26.56px margin box. Its container
	   .image-container is 24px, and .forecast-person clips overflow, so roughly 1.3px is shaved off
	   each point and the hexagon reads as a flat-bottomed slab. Giving the container the height the
	   shape actually needs leaves nothing to clip; the row is 60px, so there is room. Scoped here
	   rather than fixed in the shared component, whose other callers are not this ticket's to
	   change - and deliberately not overflow:visible on .forecast-person, which would also
	   defeat the ellipsis it uses for long names. */
	.forecast-person .image-container {
		/* !important is load-bearing, not laziness: Person sets this container's height as an INLINE
		   style, which no stylesheet rule can outrank. 27px clears the hexagon's 26.56px margin box
		   (13.28px band plus 6.64px of reserved point on each side). */
		height: 27px !important;
	}
`;

const PersonsCell = ({persons}: {persons: {id: string; name: string; avatarUrl?: string}[]}): JSX.Element => (
	<PersonsCellWrapper>
		{persons.map((person, idx) => (
			<TooltipContainer key={person.id || `${person.name}-${idx}`} translatedMessage={true} infoText={person.name}>
				<Person
					name={person.name}
					imageSrc={person.avatarUrl}
					showName={persons.length === 1}
					showRole={false}
					hideTitle={true}
				/>
			</TooltipContainer>
		))}
	</PersonsCellWrapper>
);

/** `path` and `rowType` are what let an absent value say what is absent: a task row's unset sprint
 * reads `No sprint`. A caller that passes neither keeps the plain `N/A` for every absent value. */
export const CellRenderer = ({
	value,
	path,
	rowType,
}: {
	value: CellValue | undefined;
	path?: string;
	rowType?: RowType;
}): JSX.Element => {
	const unsetMessageId =
		path !== undefined && isUnsettableRow(rowType) ? EMPTY_VALUE_MESSAGE_ID_BY_PATH.get(path) : undefined;
	const isAuditRow = rowType === 'timeRegAuditEvent';
	const empty = isAuditRow ? (
		<AuditEmpty />
	) : unsetMessageId === undefined ? (
		<NotAvailable />
	) : (
		<Unset messageId={unsetMessageId} />
	);
	if (value === undefined) {
		return empty;
	}
	if (value.kind === 'na') {
		return isAuditRow && value.reason === undefined ? <AuditEmpty /> : <NotApplicable reason={value.reason} />;
	}

	switch (value.kind) {
		case 'text':
			// Every text-kind cell - including the project-status note, which is prose rather than
			// a label - clamps to one line via the DS `ellipsis` prop, with `tooltipOnEllipsis`
			// showing the full value once it is actually cut off. This table enforces a fixed row
			// height a wrapped note cannot fit inside, so truncating is how the note stays readable
			// without breaking that limit: the full text is one hover away instead of lost.
			return (
				<Text size={2} ellipsis tooltipOnEllipsis>
					{value.value}
				</Text>
			);
		case 'number':
			return <Text size={2}>{value.value}</Text>;
		case 'boolean':
			return <BooleanCell value={value.value} />;
		case 'percent':
			// The unit is part of the value: the catalog declares this field a percentage, and a bare
			// number in a Progress column reads as a count.
			return <Text size={2}>{`${value.value}%`}</Text>;
		case 'date':
			// Clamped like text: a zoned time ("25 Aug 2026, 16:11 GMT+10") outgrows a narrowed or saved
			// column width, and the fixed row height cannot hold a second line.
			return (
				<Text size={2} ellipsis tooltipOnEllipsis>
					{value.value}
				</Text>
			);
		case 'duration':
			// A zero estimate is an estimate nobody set, and `0p` would read as one of zero.
			if (isUnestimated(path, value)) {
				return <UnestimatedNote detailsId="ai_report_builder.unestimated.details" />;
			}
			return <Text size={2}>{formatDuration(value.minutes, value.unit)}</Text>;
		case 'project':
			return (
				<ProjectCell
					id={value.id}
					name={value.name}
					projectKey={value.projectKey}
					color={value.color}
					href={value.href}
				/>
			);
		case 'status': {
			const glyph = RAG_ICON[value.rag];
			return (
				<StatusCellWrapper>
					<Text size={2}>{value.label}</Text>
					{glyph ? <Icon icon={glyph.icon} size={'s'} color={glyph.color} /> : null}
				</StatusCellWrapper>
			);
		}
		case 'persons':
			return value.persons.length === 0 ? empty : <PersonsCell persons={value.persons} />;
		case 'tag':
			return value.tags.length === 0 ? (
				<NotAvailable />
			) : (
				// LabelGroup reads `entry.label.name` / `entry.label.color`, so each tag is wrapped as the
				// join row it expects. `noWrap` turns on its overflow handling: it measures scrollWidth
				// against clientWidth and shows a tooltip with the full list plus a trailing dots
				// indicator, the same truncate-then-tooltip behaviour the other cells get from
				// `TruncatedCellContent`.
				<LabelsCellWrapper>
					<LabelGroup labels={value.tags.map(tag => ({label: tag}))} noWrap={true} />
				</LabelsCellWrapper>
			);
		default:
			return <NotAvailable />;
	}
};
